import { useEffect, useId, useRef, useState } from 'react';
import {
  Check,
  ChevronDown,
  GLASS_CLASSES,
  GLASS_SURFACE,
  Image,
} from '../../../modules/design-system';
import type { HeaderCategory } from '../../../modules/design-system';

export interface CategoryDropdownPillProps {
  categories: HeaderCategory[];
  activeId?: string;
  onSelect?: (id: string) => void;
}

/**
 * Mobile-only category selector — Figma `581:564`.
 *
 * Glass pill (active category's icon + label + chevron) that toggles a
 * listbox popover anchored just below it. Click on a category fires
 * `onSelect(id)` and closes the popover. Click-outside, Escape, and
 * tab-out also close it.
 */
export function CategoryDropdownPill({
  categories,
  activeId,
  onSelect,
}: CategoryDropdownPillProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const listboxId = `cat-listbox-${useId().replace(/:/g, '')}`;

  // Defensive against duplicate IDs in backend data: the active item is the
  // FIRST category whose `id` matches `activeId`. Index-based selection
  // below uses this same lookup so only one row in the listbox shows as
  // "selected" even if several rows share an id.
  const activeIdx = Math.max(
    0,
    activeId !== undefined ? categories.findIndex((c) => c.id === activeId) : 0,
  );
  const active = categories[activeIdx];

  // Close on outside click + Escape.
  useEffect(() => {
    if (!open) return;
    const onDocDown = (e: MouseEvent | TouchEvent) => {
      if (!rootRef.current) return;
      if (e.target instanceof Node && rootRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('touchstart', onDocDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('touchstart', onDocDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!active) return null;

  const handleSelect = (id: string) => {
    onSelect?.(id);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        data-co-component="CategoryDropdownPill"
        style={{ color: 'var(--co-CategoryDropdownPill-idleText, #fff)' }}
        className={
          GLASS_CLASSES +
          ' relative h-9 cursor-pointer overflow-hidden ' +
          (active.iconSrc ? 'pl-[40px]' : 'pl-4') +
          ' pr-2 inline-flex items-center gap-1 ' +
          'text-[13px] font-semibold tracking-[-0.1px] text-white whitespace-nowrap ' +
          'transition-opacity hover:opacity-90 active:scale-[0.98]'
        }
      >
        {active.iconSrc && (
          <Image
            src={active.iconSrc}
            alt=""
            className="pointer-events-none absolute left-[2px] h-[40px] w-[36px] top-[calc(50%+4px)] -translate-y-1/2"
            imgClassName="absolute block max-w-none"
            imgStyle={{
              height: '164%',
              width: '178%',
              left: '-39%',
              top: '-37%',
            }}
          />
        )}

        <span className="relative">{active.label}</span>

        <ChevronDown
          size={20}
          className={
            'relative shrink-0 transition-transform duration-150 ' +
            (open ? 'rotate-180' : '')
          }
        />
      </button>

      {open && (
        <ul
          id={listboxId}
          role="listbox"
          aria-label="Categories"
          className={
            GLASS_SURFACE +
            ' absolute left-0 top-[calc(100%+8px)] z-50 ' +
            'min-w-[200px] max-h-[60vh] overflow-y-auto ' +
            'rounded-[18px] p-1.5 list-none ' +
            'shadow-[0_12px_32px_rgba(0,0,0,0.25)]'
          }
        >
          {categories.map((cat, idx) => {
            const isActive = idx === activeIdx;
            return (
              <li key={`${cat.id}-${idx}`} role="none">
                <button
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  onClick={() => handleSelect(cat.id)}
                  style={{
                    background: isActive
                      ? 'var(--co-CategoryDropdownPill-activeFill, rgba(255,255,255,0.15))'
                      : undefined,
                    color: isActive
                      ? 'var(--co-CategoryDropdownPill-activeText, #fff)'
                      : 'var(--co-CategoryDropdownPill-idleText, #fff)',
                  }}
                  className={
                    'relative w-full flex items-center gap-2 ' +
                    'h-9 pl-2 pr-3 rounded-xl ' +
                    'text-[13px] font-semibold text-white whitespace-nowrap ' +
                    'transition-colors cursor-pointer ' +
                    (isActive ? '' : 'hover:bg-white/10')
                  }
                >
                  {cat.iconSrc ? (
                    <span className="relative inline-block h-7 w-7 shrink-0 overflow-hidden">
                      <Image
                        src={cat.iconSrc}
                        alt=""
                        className="pointer-events-none absolute inset-0"
                        imgClassName="absolute block max-w-none"
                        imgStyle={{
                          height: '164%',
                          width: '178%',
                          left: '-39%',
                          top: '-37%',
                        }}
                      />
                    </span>
                  ) : (
                    <span className="inline-block h-7 w-7 shrink-0" aria-hidden />
                  )}
                  <span className="relative flex-1 text-left">{cat.label}</span>
                  {isActive && (
                    <Check
                      size={16}
                      className="shrink-0"
                      style={{ color: 'var(--co-CategoryDropdownPill-tickColor, currentColor)' }}
                    />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
